import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { NavigationExtras, Params } from '@angular/router';
import { BehaviorSubject, merge, Observable, of, Subject } from 'rxjs';
import { map, mergeMap, startWith, switchMap, take, tap } from 'rxjs/operators';

import { NotificationItem } from '../components/notification-center/notification-item/notification-item.service';
import {
  BASE_PATH,
  ClinicalCase,
  Configuration,
  ElaborationWithRelations,
  ImageControllerApi,
  MessageSecondOpinionWithRelations,
  ReportControllerApi,
  SecondOpinionWithRelations,
  UserNotificationControllerApi,
  VideoUrl,
} from '../sdk-api';
import { VideoControllerApi } from '../sdk-api/api/videoController.service';
import { NotificationFilter } from '../sdk-api/model/notificationFilter';
import { NotificationWithRelations } from '../sdk-api/model/notificationWithRelations';
import { VideoConfigurationRequirements } from '../sdk-api/model/videoConfigurationRequirements';
import { AuthenticatedUser } from './authenticated-user.service';
import { ConfigurationService } from './configuration.service';
import { SecondOpinionService } from './second-opinion.service';
import { SocketIoService } from './socket-io.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService extends UserNotificationControllerApi {
  notificationCenterOpened$ = new BehaviorSubject(false);
  requirements: VideoConfigurationRequirements;
  notifications$ = new BehaviorSubject<NotificationWithRelations[]>([]);
  newNotification$ = new Subject<NotificationWithRelations>();
  notificationUpdated$ = new Subject<NotificationWithRelations | void>();
  cachedUnreadNotification$ = new BehaviorSubject<number>(0);
  hasMoreData = true;
  filter: NotificationFilter;

  constructor(
    private authenticatedUser: AuthenticatedUser,
    private configurationService: ConfigurationService,
    private videoService: VideoControllerApi,
    private imageService: ImageControllerApi,
    private secondOpinionService: SecondOpinionService,
    private reportControllerApi: ReportControllerApi,
    private socketService: SocketIoService,
    protected httpClient: HttpClient,
    @Optional() @Inject(BASE_PATH) basePath: string,
    @Optional() configuration: Configuration,
  ) {
    super(httpClient, basePath, configuration);

    this.configurationService.video
      .pipe(
        take(1),
        map((conf) => conf.requirements),
      )
      .subscribe({
        next: (requirements) => (this.requirements = requirements),
      });
  }

  get unreadNotification$(): Observable<number> {
    return this.notificationUpdated$.pipe(
      startWith(0),
      mergeMap(() =>
        this.userNotificationControllerCount(
          JSON.stringify({ read: false }) as never,
        ).pipe(
          map((res) => res.count),
          tap((count) => {
            this.cachedUnreadNotification$.next(count);
            return count;
          }),
        ),
      ),
    );
  }

  registerNewNotificationFromSecondOpinion(): Observable<NotificationWithRelations> {
    return this.socketService
      .fromEvent<{
        message: MessageSecondOpinionWithRelations;
        notification: NotificationWithRelations;
      }>('second-opinion:message:new')
      .pipe(
        switchMap((res) => {
          const { message, notification } = res;
          return this.secondOpinionService.selectedSecondOpinion$
            .asObservable()
            .pipe(
              switchMap((secondOpinion) => {
                if (
                  secondOpinion &&
                  message.secondOpinionId === secondOpinion.id
                ) {
                  return this.userNotificationControllerUpdateNotificationRead(
                    notification.id,
                  ).pipe(
                    map(() => {
                      notification.read = true;
                      return notification;
                    }),
                  );
                } else {
                  return of(notification);
                }
              }),
            );
        }),
      );
  }

  registerNewNotification(): void {
    merge(
      this.registerNewNotificationFromSecondOpinion(),
      this.socketService.fromEvent('notification:new'),
    ).subscribe({
      next: (notification: NotificationWithRelations) => {
        this.newNotification$.next(notification);
        const notifications = this.notifications$.getValue();
        notifications.unshift(notification);
        this.notifications$.next(notifications);
        this.notificationUpdated$.next(notification);
      },
    });
  }

  getNotificationActionUrl(
    notification: NotificationWithRelations,
  ): Observable<RouterNavigation> {
    switch (notification.entityName) {
      case 'Elaboration':
        return this.getUrlElaborationNotification(notification);
      case 'Video':
        return this.getUrlVideoNotification(notification);
      case 'SecondOpinion':
        return this.getUrlSecondOpinionNotification(notification);
      case 'Image':
        return this.getUrlImageNotification(notification);
      case 'Dicom':
        return this.getUrlDicomExtractionNotification(notification);
    }
  }

  getUrlElaborationNotification(
    notification: NotificationWithRelations,
  ): Observable<RouterNavigation> {
    const { extra, entity } = notification;
    const { clinicalCase } = entity as ElaborationWithRelations;
    const { elaborationId } = extra;
    const { projectId, patientId, id: clinicalCaseId } = clinicalCase;
    let prefixCommands: string[];

    if (typeof patientId === 'string') {
      prefixCommands = ['patients', patientId];
    } else if (typeof projectId === 'string') {
      prefixCommands = ['projects', projectId];
    } else {
      prefixCommands = [];
    }

    return of({
      commands: [
        ...prefixCommands,
        ...['clinical-cases', clinicalCaseId, 'view'],
      ],
      extras: { queryParams: { elaborationId } },
    });
  }

  getUrlVideoNotification(
    notification: NotificationWithRelations,
  ): Observable<RouterNavigation> {
    const { entityId: videoId } = notification;
    return this.videoService
      .videoControllerFindById(
        videoId,
        JSON.stringify({ include: [{ relation: 'clinicalCase' }] }) as unknown,
      )
      .pipe(
        map((video) => {
          const { extra } = notification;
          const { elaborationId } = extra;
          const { clinicalCase } = video;
          const { projectId, patientId, id: clinicalCaseId } = clinicalCase;
          let prefixCommands: string[];

          if (typeof patientId === 'string') {
            prefixCommands = ['patients', patientId];
          } else if (typeof projectId === 'string') {
            prefixCommands = ['projects', projectId];
          } else {
            prefixCommands = [];
          }

          return {
            commands: [
              ...prefixCommands,
              ...['clinical-cases', clinicalCaseId, 'view'],
            ],
            extras: { queryParams: { videoId, elaborationId } },
          };
        }),
      );
  }

  getUrlImageNotification(
    notification: NotificationWithRelations,
  ): Observable<RouterNavigation> {
    const { entityId: imageId } = notification;
    return this.imageService
      .imageControllerFindById(
        imageId,
        JSON.stringify({ include: [{ relation: 'clinicalCase' }] }) as unknown,
      )
      .pipe(
        map((image) => {
          const { clinicalCase, videoId, elaborationId } = image;
          const { projectId, patientId, id: clinicalCaseId } = clinicalCase;
          let prefixCommands: string[];

          if (patientId) {
            prefixCommands = ['patients', patientId];
          } else if (projectId) {
            prefixCommands = ['projects', projectId];
          } else {
            prefixCommands = [];
          }

          const queryParams: Params = { imageId };

          if (videoId) {
            queryParams.videoId = videoId;
          }

          if (elaborationId) {
            queryParams.elaborationId = elaborationId;
          }

          return {
            commands: [
              ...prefixCommands,
              ...['clinical-cases', clinicalCaseId, 'view'],
            ],
            extras: { queryParams },
          };
        }),
      );
  }

  getUrlSecondOpinionNotification(
    notification: NotificationWithRelations,
  ): Observable<RouterNavigation> {
    const entity = notification.entity as SecondOpinionWithRelations;

    const { clinicalCase, id: secondOpinionId } = entity;
    const { projectId, patientId, id: clinicalCaseId } = clinicalCase;
    if (this.authenticatedUser.user.id === entity.userId) {
      const prefixCommands = patientId
        ? ['patients', patientId]
        : ['projects', projectId];
      return of({
        commands: [
          ...prefixCommands,
          ...['clinical-cases', clinicalCaseId, 'view'],
        ],
        extras: { queryParams: { secondOpinionId } },
      });
    }

    return of({
      commands: ['second-opinions', secondOpinionId],
    });
  }

  getUrlReport(notification: NotificationItem): Observable<VideoUrl> {
    return this.reportControllerApi.reportControllerUrl(notification.entityId);
  }

  getUrlDicomExtractionNotification(
    notification: NotificationWithRelations,
  ): Observable<RouterNavigation> {
    const { entity } = notification;
    const { clinicalCase } = entity as { clinicalCase: ClinicalCase };
    const { projectId, patientId, id: clinicalCaseId } = clinicalCase;
    let prefixCommands: string[];

    if (typeof patientId === 'string') {
      prefixCommands = ['patients', patientId];
    } else if (typeof projectId === 'string') {
      prefixCommands = ['projects', projectId];
    } else {
      prefixCommands = [];
    }

    return of({
      commands: [
        ...prefixCommands,
        ...['clinical-cases', clinicalCaseId, 'view'],
      ],
    });
  }

  clearData(): void {
    this.filter = undefined;
    this.hasMoreData = true;
    this.notifications$.next([]);
  }
}

export interface RouterNavigation {
  commands: unknown[];
  extras?: NavigationExtras;
}
